import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { listFeedback } from "@/lib/feedback.functions";
import { listFeedbackWithClassification, classifyFeedback } from "@/lib/beta.functions";
import { PageHeader } from "@/components/admin/AdminPrimitives";
import { Star } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/feedback")({
  head: () => ({ meta: [{ title: "Feedback — Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: FeedbackPage,
});

function FeedbackPage() {
  const fn = useServerFn(listFeedback);
  const [days, setDays] = useState(30);
  const [scope, setScope] = useState<string>("");
  const [onlyComment, setOnlyComment] = useState(false);
  const q = useQuery({
    queryKey: ["admin-feedback", days, scope, onlyComment],
    queryFn: () => fn({ data: { days, scope: (scope || undefined) as never, onlyWithComment: onlyComment } }),
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Feedback do membro" description="Notas, comentários e sentimento nos últimos períodos." />

      <div className="flex flex-wrap items-center gap-2">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)}
            className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${
              days === d ? "border-[var(--navy)] bg-[var(--navy)] text-white" : "border-black/10 text-[var(--gray-ink)] hover:bg-[var(--gray-soft)]"
            }`}>{d}d</button>
        ))}
        <select value={scope} onChange={(e) => setScope(e.target.value)}
          className="rounded-md border border-black/10 px-2 py-1.5 text-xs">
          <option value="">Todos os escopos</option>
          <option value="platform">Plataforma</option>
          <option value="business">Negócio</option>
          <option value="stage">Etapa</option>
          <option value="mission">Missão</option>
          <option value="task">Tarefa</option>
          <option value="journey">Jornada</option>
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={onlyComment} onChange={(e) => setOnlyComment(e.target.checked)} />
          Só com comentário
        </label>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Kpi label="Total" value={q.data?.summary.total ?? 0} />
        <Kpi label="Nota média" value={q.data?.summary.avgRating ?? "—"} />
        <Kpi label="Útil" value={q.data?.summary.helpful ?? 0} />
        <Kpi label="Não útil" value={q.data?.summary.notHelpful ?? 0} />
      </div>

      <div className="rounded-xl border border-black/[0.06] bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
              <th className="px-4 py-3">Quando</th><th className="px-4 py-3">Escopo</th>
              <th className="px-4 py-3">Nota</th><th className="px-4 py-3">Comentário</th>
            </tr>
          </thead>
          <tbody>
            {(q.data?.items ?? []).map((f: any) => (
              <tr key={f.id} className="border-t border-black/5 align-top">
                <td className="px-4 py-3 text-[11px] text-[var(--gray-ink)]">{new Date(f.created_at).toLocaleString("pt-BR")}</td>
                <td className="px-4 py-3 text-xs font-semibold">{f.scope}</td>
                <td className="px-4 py-3">
                  {f.rating ? (
                    <span className="inline-flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} size={12} className={i < f.rating ? "fill-[var(--gold)] text-[var(--gold)]" : "text-black/15"} />
                      ))}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 text-sm">{f.comment ?? <span className="text-[var(--gray-ink)]">—</span>}</td>
              </tr>
            ))}
            {(q.data?.items ?? []).length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-[var(--gray-ink)]">Nenhum feedback no período.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ClassificationSection />
    </div>
  );
}

const CATEGORIES = [
  "novo",
  "elogio",
  "duvida",
  "problema_conteudo",
  "problema_tecnico",
  "problema_usabilidade",
  "sugestao",
] as const;
const STATES = ["novo", "em_analise", "planejado", "resolvido", "nao_sera"] as const;

function ClassificationSection() {
  const qc = useQueryClient();
  const listFn = useServerFn(listFeedbackWithClassification);
  const classifyFn = useServerFn(classifyFeedback);
  const q = useQuery({ queryKey: ["feedback-classify"], queryFn: () => listFn() });
  const m = useMutation({
    mutationFn: (v: { feedback_id: string; category: any; state: any; is_blocker: boolean }) =>
      classifyFn({ data: v }),
    onSuccess: () => {
      toast.success("Classificação salva.");
      qc.invalidateQueries({ queryKey: ["feedback-classify"] });
    },
  });

  return (
    <section className="rounded-xl border border-black/[0.06] bg-white">
      <header className="border-b border-black/5 px-4 py-3">
        <h2 className="text-sm font-semibold text-[var(--navy)]">Classificação operacional</h2>
        <p className="text-[11px] text-[var(--gray-ink)]">
          Texto original preservado. Categoria, estado e bloqueio são visíveis apenas para administradores.
        </p>
      </header>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
            <th className="px-4 py-3">Comentário</th>
            <th className="px-4 py-3">Categoria</th>
            <th className="px-4 py-3">Estado</th>
            <th className="px-4 py-3">Bloqueante</th>
          </tr>
        </thead>
        <tbody>
          {(q.data?.items ?? []).slice(0, 100).map((f: any) => {
            const c = f.classification ?? { category: "novo", state: "novo", is_blocker: false };
            return (
              <tr key={f.id} className="border-t border-black/5 align-top">
                <td className="max-w-[380px] px-4 py-3 text-xs">
                  <div className="font-medium">{f.scope}</div>
                  <div className="mt-0.5 whitespace-pre-wrap text-[var(--gray-ink)]">
                    {f.comment ?? "—"}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <select
                    defaultValue={c.category}
                    onChange={(e) =>
                      m.mutate({
                        feedback_id: f.id,
                        category: e.target.value,
                        state: c.state,
                        is_blocker: c.is_blocker,
                      })
                    }
                    className="rounded-md border border-black/10 px-2 py-1 text-xs"
                  >
                    {CATEGORIES.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select
                    defaultValue={c.state}
                    onChange={(e) =>
                      m.mutate({
                        feedback_id: f.id,
                        category: c.category,
                        state: e.target.value,
                        is_blocker: c.is_blocker,
                      })
                    }
                    className="rounded-md border border-black/10 px-2 py-1 text-xs"
                  >
                    {STATES.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    defaultChecked={c.is_blocker}
                    onChange={(e) =>
                      m.mutate({
                        feedback_id: f.id,
                        category: c.category,
                        state: c.state,
                        is_blocker: e.target.checked,
                      })
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function Kpi({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-black/[0.06] bg-white p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">{label}</p>
      <p className="mt-1 font-display text-2xl font-extrabold text-[var(--navy)]">{value}</p>
    </div>
  );
}
